import { useMutation, useQuery } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { useStepSave } from "../../../../hooks/useStepSave";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import BuscadorInsumo from "./components/BuscadorInsumo";
import useGetInsumos from "../../../../hooks/useGetInsumos";
import { handleChangeImageVisita } from "../../../../utils/handleChangeImageVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import SAVE_VISITA_NPID_MEDICO, { adaptAddVisitaNpidMedicoInput } from "../../../../../../data/graphQL/mutations/Visitas/saveVisitaNpidMedico";
import toast from "react-hot-toast";

const ID_TIPO_BAP_ESTANDAR = 334;
const ID_TIPO_BAP_MAGISTRAL = 335;

export default function ProgramaIntradialisisMedico({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo, esUrgencia }) {
  const { deleteArchivo } = useDeleteArchivoVisita();
  const [formData, setFormData] = useState({});
  const [combosRI, setCombosRI] = useState();
  const inputCombos = groups.flatMap((g) => g.inputs.filter((i) => !!i.comboName));

  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI))
  }, [dataCombosRI]);

  const [save] = useMutation(SAVE_VISITA_NPID_MEDICO);

  const {
    insumos,
    nombreInsumoSelected,
    filterStatus,
    insumosLoading,
    setFilterStatus,
    setNombreInsumoSelected
  } = useGetInsumos(visita.idEvaluacion);

  const formattedGroups = useMemo(() => {
    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        if (!!newInput.comboName) {
          newInput.options = combosRI?.[input.name];
        }

        switch (input.name) {
          case "justificacionInsercion":
            newInput.hidden = formData?.insercionNormal !== false;
            newInput.required = formData?.insercionNormal === false;
            break;

          case "idInsumo":
          case "volumenEstandar":
            newInput.hidden = formData?.bAPEstandar !== true;
            break;
          case "diasConexionSemanaEstandar":
            newInput.options = newInput.options.map((o) => ({ ...o, disabled: formData.diasConexionSemanaMagistral?.includes(o.value) }));
            newInput.hidden = formData?.bAPEstandar !== true;
            break;

          case "volumenMagistral":
          case "idComboRIValorMotivoMagistral":
          case "indicacionComentario":
            newInput.hidden = formData?.bAPMagistral !== true;
            break;
          case "diasConexionSemanaMagistral":
            newInput.options = newInput.options.map((o) => ({ ...o, disabled: formData.diasConexionSemanaEstandar?.includes(o.value) }));
            newInput.hidden = formData?.bAPMagistral !== true;
            break;
          case "complicaciones":
            const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
            newInput.options = complicacionesCombo?.map((opt) => {
              if (Array.isArray(formData.complicaciones)) {
                const hasNinguna = formData.complicaciones.includes(ningunaId);
                const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
                if (hasNinguna) {
                  // Si "ninguna" está seleccionada, deshabilitar todas menos "ninguna"
                  return { ...opt, disabled: opt.id !== ningunaId };
                }
                if (hasOther) {
                  // Si hay alguna distinta de "ninguna", deshabilitar "ninguna"
                  return { ...opt, disabled: opt.id === ningunaId };
                }
              }
              return { ...opt, disabled: false };
            });
            break;
          default:
            break;
        }

        if (readOnly) newInput.disabled = true;
        if (esUrgencia) newInput.required = false;

        return newInput;
      });

      const hidden = !formattedInputs.some((i) => !i.hidden);
      return { ...group, hidden, inputs: formattedInputs };
    });
  }, [readOnly, formData, combosRI, complicacionesCombo,
    formData?.insercionNormal,
    formData?.complicaciones,
    formData?.idInsumo,
    formData?.diasConexionSemanaEstandar,
    formData?.diasConexionSemanaMagistral,
    formData?.idComboRIValorAcceso,
    esUrgencia]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeFile = useCallback((name, fileValue) => {
    handleChangeImageVisita({
      saveArchivoVisita,
      name,
      fileValue,
      idVisita: visita.idVisita,
      handleChange,
      idTipoArchivo: tipoArchivoVisita.LABORATORIO
    });
  }, [visita.idVisita, saveArchivoVisita]);

  const onDeleteImage = (name) => {
    const file = formData[name];
    deleteArchivo({
      idArchivo: file.idArchivo,
      name,
      handleChange
    });
  };

  const handleSelectInsumo = (newValue) => {
    handleChange('idInsumo', newValue?.id);
    setNombreInsumoSelected(newValue?.label);
  };

  useStepSave(onSaveRef, async (validate) => {
    let error;
    // debe seleccionar BAP Estandar o BAP Magistral, al menos una de las dos
    if (formData?.bAPEstandar === false && formData?.bAPMagistral === false) {
      error = "Debe seleccionar al menos una indicación.";
    }
    if (!!error) {
      toast.error(error);
      const errorSave = new Error(error);
      errorSave.isCustomError = true;
      throw errorSave;
    }
    if (validate) validateStepFields({ formattedGroups, formData });
    const { data } = await save({ variables: { input: adaptAddVisitaNpidMedicoInput(formData) } });
    const idVisitaNpid = data?.saveVisitaNpidMedico?.idVisitaNpid;
    if (!idVisitaNpid) throw new Error("Error al guardar");
    setVisita((prev) => ({ ...prev, ...formData, idVisitaNpid }));
  });

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);
      setFormData({
        ...filterVisitaData(visita, inputNames),
        diasConexionSemanaEstandar: visita.diasConexion?.filter((d) => d.idComboRIValorTipoBAP === ID_TIPO_BAP_ESTANDAR).map((d) => d.dia) || [],
        diasConexionSemanaMagistral: visita.diasConexion?.filter((d) => d.idComboRIValorTipoBAP === ID_TIPO_BAP_MAGISTRAL).map((d) => d.dia) || [],
        idVisita: visita.idVisita,
        idVisitaNpid: visita.idVisitaNpid
      });
    }
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaIntradialisisMedico",
      stepName: "Programa Intradialisis (NPID)",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
      {formattedGroups.
        filter((group) => !group.hidden).
        map((group, idx) =>
          <CardBox key={idx} title={group.title}>
            <Grid container spacing={2}>
              {group.inputs.
                filter((input) => !input.hidden).
                map((input) =>
                  <Grid size={{ xs: 12 }} key={input.name}>
                    {input.name !== 'idInsumo' &&
                      <RenderInputByTypeMobile
                        input={input}
                        value={formData[input.name]}
                        handleChange={input.type === 'file' ? handleChangeFile : handleChange}
                        handleDelete={input.type === 'file' ? onDeleteImage : null}
                        row={input.type === "multiple_boolean"} />

                    }
                    {input.name === 'idInsumo' &&
                      <BuscadorInsumo
                        insumos={insumos}
                        input={input}
                        idInsumo={formData.idInsumo}
                        nombreInsumoSelected={nombreInsumoSelected}
                        filterStatus={filterStatus}
                        setFilterStatus={setFilterStatus}
                        handleSelectInsumo={handleSelectInsumo}
                        insumosLoading={insumosLoading} />

                    }
                  </Grid>
                )}
            </Grid>
          </CardBox>
        )}
    </>);

}

const groups = [
  {
    title: "Accesos",
    inputs: [
      {
        displayName: 'Acceso',
        name: 'idComboRIValorAcceso',
        comboName: 'Tipo de Catéter AP',
        type: 'select',
        required: false,
        disabled: false
      },
      {
        name: "fechaColocacion",
        displayName: "Fecha de colocación",
        type: "date",
        disableFuture: true,
        required: true
      },
      {
        name: "idComboRIValorUbicacion",
        displayName: "Ubicación del catéter",
        comboName: 'Ubicación del catéter AP',
        type: "select",
        required: true
      },
      {
        name: "idComboRIValorLumenes",
        displayName: "N° de lúmenes",
        comboName: 'N° de Luces',
        type: "select",
        required: true
      },
      {
        name: "insercionNormal",
        displayName: "¿Sitio de inserción de catéter normal?",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "justificacionInsercion",
        displayName: "Si es NO, justificar",
        type: "long_string",
        maxLength: 4000
      }]

  },
  {
    title: "Indicaciones",
    inputs: [
      {
        name: "bAPEstandar",
        displayName: "BAP estándar",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "idInsumo",
        displayName: "¿Cuál?",
        type: "search"
      },
      {
        name: "volumenEstandar",
        displayName: "Volumen (ml)",
        type: "float",
        required: true,
        min: 0
      },
      {
        name: "diasConexionSemanaEstandar",
        displayName: "Conexiones x semana",
        type: "checkbox",
        options: [
          { value: 1, label: "L" },
          { value: 2, label: "M" },
          { value: 3, label: "X" },
          { value: 4, label: "J" },
          { value: 5, label: "V" },
          { value: 6, label: "S" },
          { value: 7, label: "D" }],

        lightTitle: true,
        selectAllOption: true,
        required: true
      },

      {
        name: "bAPMagistral",
        displayName: "BAP magistral",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "volumenMagistral",
        displayName: "Volumen (ml)",
        type: "float",
        required: true,
        min: 0
      },
      {
        name: "idComboRIValorMotivoMagistral",
        displayName: "Motivo de la indicación Magistral",
        comboName: 'Motivo de la indicación Estandar AP',
        type: "select",
        required: false
      },
      {
        name: "diasConexionSemanaMagistral",
        displayName: "Conexiones x semana",
        type: "checkbox",
        options: [
          { value: 1, label: "L" },
          { value: 2, label: "M" },
          { value: 3, label: "X" },
          { value: 4, label: "J" },
          { value: 5, label: "V" },
          { value: 6, label: "S" },
          { value: 7, label: "D" }],

        lightTitle: true,
        selectAllOption: true,
        required: true
      },
      {
        name: "indicacionComentario",
        displayName: "Comentarios de indicación",
        type: "long_string",
        maxLength: 4000
      }]

  },
  {
    title: "Requerimientos",
    inputs: [
      {
        name: "proteinasTotales",
        displayName: "Proteínas totales/d",
        type: "float",
        required: true
      },
      {
        name: "lipidosTotales",
        displayName: "Lípidos totales/d",
        type: "float",
        required: true
      },
      {
        name: "caloriasTotales",
        displayName: "Calorías totales/d",
        type: "float",
        required: true
      },
      {
        name: "laboratorioAdjunto",
        displayName: "Laboratorio (adjuntar)",
        type: "file"
      },
      {
        name: "complicaciones",
        displayName: "Complicaciones",
        type: "multiple_select",
        required: true
      }]

  }];